import { Component, type ErrorInfo, type ReactNode } from 'react';
import { analytics } from '@/lib/analytics';
import { isChunkLoadError, reloadForStaleChunk } from '@/lib/stale-chunk-reload';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // A chunk from before a deploy: reload once to pick up the new build (stale-chunk-reload.ts).
    // If that already happened moments ago, fall through and show the error.
    if (isChunkLoadError(error) && reloadForStaleChunk()) {
      return;
    }

    analytics.captureException(error, {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

const DefaultErrorFallback = ({ error }: { error: Error | null }) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-6 text-center">
      <h2 className="mb-2 text-2xl font-bold text-red-600">{t('error')}</h2>
      <p className="mb-6 text-gray-600">
        {error?.message ?? 'Something went wrong. Please try again later.'}
      </p>
      <button
        onClick={() => window.location.reload()}
        className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
      >
        {t('retry')}
      </button>
    </div>
  );
};
